import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Plus, Trash2, Loader2, Save, Pencil, BookOpenCheck,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * MaterialCatalogManager
 * -----------------------
 * Settings-side manager for reusable materials. Workers pick from this list
 * on the job materials panel (Phase 3 — Feb 2026).
 *
 * Tight scope: description + default cost + default markup % + unit label.
 * No inventory levels, no SKUs — keep it a dictionary, not a warehouse.
 */
export default function MaterialCatalogManager({ token }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ description: '', default_cost: '', default_markup_pct: '', unit_label: 'ea' });
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/material-catalog`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data.items || []);
    } catch (err) {
      toast({ title: 'Could not load catalog', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleAdd = async () => {
    if (!draft.description.trim()) {
      toast({ title: 'Description required', variant: 'destructive' });
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/material-catalog`, {
        description: draft.description.trim(),
        default_cost: draft.default_cost === '' ? 0 : parseFloat(draft.default_cost),
        default_markup_pct: draft.default_markup_pct === '' ? null : parseFloat(draft.default_markup_pct),
        unit_label: draft.unit_label || 'ea',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setDraft({ description: '', default_cost: '', default_markup_pct: '', unit_label: 'ea' });
      await reload();
      toast({ title: 'Catalog item added' });
    } catch (err) {
      toast({ title: 'Could not add', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditDraft({
      description: it.description,
      default_cost: it.default_cost ?? '',
      default_markup_pct: it.default_markup_pct ?? '',
      unit_label: it.unit_label || 'ea',
    });
  };

  const saveEdit = async () => {
    setBusyId(editingId);
    try {
      await axios.patch(`${API}/material-catalog/${editingId}`, {
        description: editDraft.description?.trim(),
        default_cost: editDraft.default_cost === '' ? 0 : parseFloat(editDraft.default_cost),
        default_markup_pct: editDraft.default_markup_pct === '' || editDraft.default_markup_pct == null ? null : parseFloat(editDraft.default_markup_pct),
        unit_label: editDraft.unit_label || 'ea',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEditingId(null);
      setEditDraft({});
      await reload();
    } catch (err) {
      toast({ title: 'Could not update', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Remove "${it.description}" from the catalog?`)) return;
    setBusyId(it.id);
    try {
      await axios.delete(`${API}/material-catalog/${it.id}`, { headers: { Authorization: `Bearer ${token}` } });
      await reload();
    } catch (err) {
      toast({ title: 'Could not delete', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-lg border p-4 sm:p-6 mt-6" style={{ background: '#0F1923', borderColor: '#2D3748' }} data-testid="material-catalog-manager">
      <div className="flex items-center gap-2 mb-1">
        <BookOpenCheck className="w-5 h-5 text-emerald-400" />
        <h3 className="text-base font-semibold text-white">Materials Catalogue</h3>
      </div>
      <p className="text-xs mb-4" style={{ color: '#94A3B8' }}>
        Pre-fill commonly purchased items. Workers tap the picker on the Materials tab of a job and the description / cost / markup auto-fill.
      </p>

      {/* Add row */}
      <div className="rounded-md border border-emerald-700/40 bg-emerald-950/20 p-3 mb-4" data-testid="catalog-add-form">
        <div className="grid grid-cols-12 gap-2">
          <Input
            value={draft.description}
            onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
            placeholder="e.g., 5/8&quot; drywall sheet 4×8"
            className="col-span-5 bg-[#1A2433] border-[#2D3748] text-white text-sm"
            data-testid="catalog-new-desc"
          />
          <Input
            type="number" step="0.01" min="0"
            value={draft.default_cost}
            onChange={(e) => setDraft(d => ({ ...d, default_cost: e.target.value }))}
            placeholder="Cost"
            className="col-span-2 bg-[#1A2433] border-[#2D3748] text-white text-sm"
            data-testid="catalog-new-cost"
          />
          <Input
            type="number" step="0.1" min="0"
            value={draft.default_markup_pct}
            onChange={(e) => setDraft(d => ({ ...d, default_markup_pct: e.target.value }))}
            placeholder="Markup %"
            className="col-span-2 bg-[#1A2433] border-[#2D3748] text-white text-sm"
            data-testid="catalog-new-markup"
          />
          <Input
            value={draft.unit_label}
            onChange={(e) => setDraft(d => ({ ...d, unit_label: e.target.value }))}
            placeholder="ea / box / hr"
            className="col-span-2 bg-[#1A2433] border-[#2D3748] text-white text-sm"
            data-testid="catalog-new-unit"
          />
          <Button
            onClick={handleAdd}
            disabled={adding || !draft.description.trim()}
            className="col-span-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2"
            data-testid="catalog-add-btn"
            title="Add to catalog"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center text-slate-400">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-6 px-3 border border-dashed border-[#2D3748] rounded-md text-center">
          No catalog items yet. Add commonly purchased materials above so workers can pick them from a list.
        </div>
      ) : (
        <div className="space-y-1" data-testid="catalog-items">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 px-1 pb-1">
            <div className="col-span-5">Description</div>
            <div className="col-span-2">Cost</div>
            <div className="col-span-2">Markup</div>
            <div className="col-span-2">Unit</div>
            <div className="col-span-1"></div>
          </div>
          {items.map((it) => {
            const isEditing = editingId === it.id;
            return (
              <div
                key={it.id}
                className="grid grid-cols-12 gap-2 items-center rounded-md px-1 py-1 hover:bg-[#1A2433]"
                data-testid={`catalog-row-${it.id}`}
              >
                {isEditing ? (
                  <>
                    <Input value={editDraft.description} onChange={(e) => setEditDraft(d => ({ ...d, description: e.target.value }))} className="col-span-5 bg-[#1A2433] border-[#2D3748] text-white text-sm" />
                    <Input type="number" step="0.01" value={editDraft.default_cost} onChange={(e) => setEditDraft(d => ({ ...d, default_cost: e.target.value }))} className="col-span-2 bg-[#1A2433] border-[#2D3748] text-white text-sm" />
                    <Input type="number" step="0.1" value={editDraft.default_markup_pct} onChange={(e) => setEditDraft(d => ({ ...d, default_markup_pct: e.target.value }))} placeholder="—" className="col-span-2 bg-[#1A2433] border-[#2D3748] text-white text-sm" />
                    <Input value={editDraft.unit_label} onChange={(e) => setEditDraft(d => ({ ...d, unit_label: e.target.value }))} className="col-span-2 bg-[#1A2433] border-[#2D3748] text-white text-sm" />
                    <div className="col-span-1 flex gap-0.5 justify-end">
                      <button type="button" onClick={saveEdit} disabled={busyId === it.id} className="p-1 text-emerald-300 hover:text-emerald-200" title="Save">
                        {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      </button>
                      <button type="button" onClick={() => { setEditingId(null); setEditDraft({}); }} className="p-1 text-slate-500 hover:text-slate-300" title="Cancel">×</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-5 text-sm text-white truncate">{it.description}</div>
                    <div className="col-span-2 text-sm text-emerald-300">${Number(it.default_cost || 0).toFixed(2)}</div>
                    <div className="col-span-2 text-sm text-slate-300">{it.default_markup_pct != null ? `${it.default_markup_pct}%` : <span className="text-slate-500">—</span>}</div>
                    <div className="col-span-2 text-sm text-slate-300">{it.unit_label || 'ea'}</div>
                    <div className="col-span-1 flex gap-0.5 justify-end">
                      <button type="button" onClick={() => startEdit(it)} className="p-1 text-slate-400 hover:text-white" title="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => handleDelete(it)} disabled={busyId === it.id} className="p-1 text-rose-400 hover:text-rose-300" title="Remove">
                        {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
